import { escapeHtml } from '../format';
import type { StopHit } from '../api';

/** Eine Linie, wie sie unter den geladenen Fahrzeugen gefunden wurde. */
export interface LineHit {
  name: string;
  bg: string;
  fg: string;
  count: number;
  /** Das dem Kartenmittelpunkt naechste Fahrzeug dieser Linie. */
  vehicleId: string;
  lat: number;
  lon: number;
}

export interface SearchHandlers {
  /** Linientreffer aus den gerade geladenen Fahrzeugen, bereits sortiert. */
  lineHits(query: string): LineHit[];
  /** Kartenmittelpunkt als [lat, lon] - der Geocoder gewichtet danach. */
  near(): [number, number];
  fetchStops(query: string, near: [number, number], signal: AbortSignal): Promise<StopHit[]>;
  onStop(hit: StopHit): void;
  onLine(hit: LineHit): void;
  onClear(): void;
}

type Entry = { kind: 'stop'; hit: StopHit } | { kind: 'line'; hit: LineHit };

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

export class SearchPanel {
  private entries: Entry[] = [];
  private active = -1;
  private timer: number | null = null;
  private request: AbortController | null = null;
  private stopsError = false;

  constructor(
    private readonly input: HTMLInputElement,
    private readonly clearButton: HTMLButtonElement,
    private readonly list: HTMLElement,
    private readonly handlers: SearchHandlers,
  ) {
    this.input.addEventListener('input', () => this.schedule());
    this.input.addEventListener('focus', () => {
      if (this.entries.length) this.list.hidden = false;
    });
    this.input.addEventListener('keydown', (event) => this.onKey(event));
    // Ohne die kleine Verzoegerung liefe das Ausblenden dem Klick zuvor.
    this.input.addEventListener('blur', () => window.setTimeout(() => this.hide(), 150));
    this.clearButton.addEventListener('click', () => this.clear());
    this.list.addEventListener('mousedown', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('[data-index]');
      if (!row) return;
      event.preventDefault();
      this.activate(Number.parseInt(row.dataset.index ?? '', 10));
    });
  }

  /** Setzt Eingabe, Liste und Kartenzustand zurueck. */
  reset(): void {
    this.input.value = '';
    this.entries = [];
    this.active = -1;
    this.hide();
    this.clearButton.hidden = true;
    this.handlers.onClear();
  }

  clear(): void {
    this.reset();
    this.input.focus();
  }

  private hide(): void {
    this.list.hidden = true;
  }

  private schedule(): void {
    this.clearButton.hidden = this.input.value.length === 0;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => void this.run(), DEBOUNCE_MS);
  }

  private async run(): Promise<void> {
    const query = this.input.value.trim();
    if (query.length < MIN_CHARS) {
      this.entries = [];
      this.hide();
      // Auch das Leertippen beendet die Suche, nicht nur der Knopf: sonst bliebe
      // die Abblendung haengen und es gaebe keinen Weg zurueck.
      this.handlers.onClear();
      return;
    }

    // Linien kommen ohne Netzabfrage, also sofort - die Liste steht schon,
    // waehrend die Haltestellen noch unterwegs sind.
    const lines = this.handlers.lineHits(query.toLowerCase());
    this.entries = lines.map((hit) => ({ kind: 'line', hit }) as Entry);
    this.stopsError = false;
    this.render();

    this.request?.abort();
    const controller = new AbortController();
    this.request = controller;
    try {
      const stops = await this.handlers.fetchStops(query, this.handlers.near(), controller.signal);
      if (controller.signal.aborted) return;
      this.entries = [
        ...stops.map((hit) => ({ kind: 'stop', hit }) as Entry),
        ...lines.map((hit) => ({ kind: 'line', hit }) as Entry),
      ];
    } catch (err) {
      if (controller.signal.aborted || (err as Error)?.name === 'AbortError') return;
      this.stopsError = true;
    }
    this.active = -1;
    this.render();
  }

  private render(): void {
    const rows: string[] = [];
    const stops = this.entries.filter((e) => e.kind === 'stop');
    const lines = this.entries.filter((e) => e.kind === 'line');

    if (stops.length || this.stopsError) {
      rows.push('<li class="search-group">Haltestellen</li>');
    }
    if (this.stopsError) {
      rows.push('<li class="search-empty">Haltestellensuche gerade nicht erreichbar</li>');
    }
    for (const entry of stops) {
      const index = this.entries.indexOf(entry);
      const hit = entry.hit as StopHit;
      rows.push(
        `<li class="search-row${index === this.active ? ' is-active' : ''}" data-index="${index}">` +
          `<span class="search-pin" aria-hidden="true">◎</span>` +
          `<span class="search-text"><b>${escapeHtml(hit.name)}</b>` +
          (hit.area ? `<span>${escapeHtml(hit.area)}</span>` : '') +
          `</span></li>`,
      );
    }

    if (lines.length) {
      rows.push('<li class="search-group">Linien · nur was geladen ist</li>');
    }
    for (const entry of lines) {
      const index = this.entries.indexOf(entry);
      const hit = entry.hit as LineHit;
      rows.push(
        `<li class="search-row${index === this.active ? ' is-active' : ''}" data-index="${index}">` +
          `<span class="search-badge" style="background:${escapeHtml(hit.bg)};color:${escapeHtml(hit.fg)}">` +
          `${escapeHtml(hit.name)}</span>` +
          `<span class="search-text"><b>${hit.count} ${hit.count === 1 ? 'Fahrzeug' : 'Fahrzeuge'}</b></span></li>`,
      );
    }

    if (rows.length === 0) {
      rows.push('<li class="search-empty">Nichts gefunden</li>');
    }

    this.list.innerHTML = `<ul>${rows.join('')}</ul>`;
    this.list.hidden = false;
  }

  private onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (this.input.value) this.reset();
      else this.hide();
      return;
    }
    if (this.list.hidden || this.entries.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.active = (this.active + step + this.entries.length) % this.entries.length;
      this.render();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.activate(this.active >= 0 ? this.active : 0);
    }
  }

  private activate(index: number): void {
    const entry = this.entries[index];
    if (!entry) return;
    this.hide();
    this.input.blur();
    if (entry.kind === 'stop') this.handlers.onStop(entry.hit);
    else this.handlers.onLine(entry.hit);
  }
}
